import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Each stat fails soft: if the endpoint errors (e.g. permissions), we show "—"
// rather than a fake hardcoded number.
const useSidebarStats = () =>
  useQuery({
    queryKey: ["sidebar-system-stats"],
    queryFn: async () => {
      const safe = async (p) => {
        try {
          const r = await api.get(p);
          return r.data;
        } catch {
          return null;
        }
      };
      const [terminals, attendance, pendingAbsences] = await Promise.all([
        safe("v1/zkteco/terminals"),
        safe(`v1/daily-attendance/?date_from=${todayISO()}&date_to=${todayISO()}&limit=1`),
        safe("v1/absences?status=pending&limit=1"),
      ]);

      let terminalsLabel = null;
      if (terminals) {
        const list = terminals.terminals || terminals.items || [];
        const online = list.filter((t) => (t.status || "").toLowerCase() === "online").length;
        terminalsLabel = `${online}/${list.length}`;
      }
      const checkIns = attendance ? attendance.total ?? null : null;
      const toValidate = pendingAbsences ? pendingAbsences.total ?? null : null;

      return { terminalsLabel, checkIns, toValidate };
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: false,
  });

const SidebarSystemCard = () => {
  const { t } = useTranslation();
  const { data } = useSidebarStats();
  const show = (v) => (v === null || v === undefined ? "—" : String(v));

  return (
    <div className="px-1">
      <p className="text-xs font-medium uppercase tracking-wider mb-2 px-2" style={{ color: "var(--lm-ink-3)", fontSize: "10px" }}>
        {t('sidebar.system.title')}
      </p>
      <div className="space-y-1 px-2">
        {[
          { label: t('sidebar.system.terminalsOnline'), value: show(data?.terminalsLabel), color: "#27a644" },
          { label: t('sidebar.system.checkInsToday'),   value: show(data?.checkIns),       color: "#5e6ad2" },
          { label: t('sidebar.system.toValidate'),      value: show(data?.toValidate),     color: "#f59e0b" },
        ].map(({ label, value, color }) => (
          <div key={label} className="flex items-center justify-between">
            <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{label}</span>
            <span className="text-xs font-semibold" style={{ color }}>{value}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4 mt-3 pt-2" style={{ borderTop: "1px solid var(--lm-border)" }}>
        {[
          { label: "API", color: "#27a644" },
          { label: "DB",  color: "#27a644" },
        ].map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            <span style={{ fontSize: "10px", color: "var(--lm-ink-3)" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SidebarSystemCard;
